import { FC, PropsWithChildren } from "react";
import { choose, visualizeWrongAndSelect, finalChoice, visualizeDoors, visualizeDoor, visualizeSelected } from "./ThreeDoorSolver";

export type TableProps = React.HTMLAttributes<HTMLDivElement> & {
  name: string;
  choses: choose[];
  reverse?: boolean;
  showTime?: boolean;
}

const Table: FC<TableProps> = ({choses, name, reverse, showTime, ...rootProps}) => {

  const toDisplay = reverse ? choses.slice().reverse() : choses;
  const finalChoses = toDisplay.map(finalChoice);

  return (
    <div {...rootProps}>
      <div className="flex flex-col">
        <h2 className="text-base sm:text-2xl text-center capitalize font-normal py-2 bg-green text-white rounded-t">{name}</h2>
        <table>
          <thead>
            <tr 
              className={`
                grid grid-cols-3 
                ${ showTime ? "md:grid-cols-8" : "md:grid-cols-7"}
              `}
            >
              <HeaderCell md={1}> try </HeaderCell>
              <HeaderCell md={2}> choose </HeaderCell>
              <HeaderCell md={3}> hint </HeaderCell>
              <HeaderCell md={4}> select </HeaderCell>
              <HeaderCell md={5}> final </HeaderCell>
              <HeaderCell md={7}> spoiler </HeaderCell>
              <HeaderCell md={6}> result </HeaderCell>
              { 
                showTime 
                  ? <HeaderCell md={8}>time</HeaderCell>
                  : <></>
              }
            </tr>
          </thead>
          <tbody>
            { 
              toDisplay.map( (c, i) => (
                <tr 
                  key={`tbl-${name}-el-${i}`} 
                  className={`
                    grid 
                    grid-cols-3 my-10 last:mb-0
                    md:my-0 
                    ${ showTime ? "md:grid-cols-8" : "md:grid-cols-7"}
                  `}
                >
                  <Cell md={1}>{reverse ? toDisplay.length - i : i+1} </Cell>
                  <Cell md={2} visualization>{visualizeSelected(c)}</Cell>
                  <Cell md={3} visualization>{visualizeWrongAndSelect(c)}</Cell>
                  <Cell md={4}>{c.answer.change ? "change" : "old"}</Cell>
                  <Cell md={5} visualization>{visualizeWrongAndSelect(finalChoice(c))}</Cell>
                  <Cell md={7} visualization>{visualizeDoors(c)}</Cell>
                  <Cell md={6}>{c.secret[finalChoses[i].answer.select]} ({visualizeDoor(c.secret[finalChoses[i].answer.select], c.id, finalChoses[i].answer.select)})</Cell>
                  { 
                    showTime 
                      ? <Cell md={8}>{c.dateWhenComplete?.toLocaleTimeString("it-IT") ?? "missed"}</Cell>
                      : <></>
                  }
                </tr>
              )) 
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

const orderMd = new Map<number, string>([
  [1, "md:order-1"],
  [2, "md:order-2"],
  [3, "md:order-3"],
  [4, "md:order-4"],
  [5, "md:order-5"],
  [6, "md:order-6"],
  [7, "md:order-7"],
  [8, "md:order-8"],
  [9, "md:order-9"],
])  

const HeaderCell: FC<PropsWithChildren<{ md?: number}>> = ({ children, md }) => {
  return (
    <th 
      className={`
        w-full md:min-ww-min capitalize border px-4 py-2
        text-center
        ${orderMd.get(md ?? 0) ?? 'order-none'}
      `}
    >
      {children}
    </th>
  );
}

const Cell: FC<PropsWithChildren<{visualization?: boolean, md?: number}>> = ({ children, visualization, md }) => {
  return (
    <td 
      className={`
        w-full md:min-ww-min border px-4 py-2
        text-center 
        ${visualization ? "text-xl" : ""}
        ${orderMd.get(md ?? 0) ?? 'order-none'}
      `}
    >
      {children}
    </td>
  );
}




export default Table;